import {
  DeterminationContent,
  Question,
  QuestionnaireListResponse,
  QuestionnaireResponsesRequest,
  QuestionnaireResponseSetCreateRequest,
} from "capitalrx-types";
import { UseFormReturn } from "capitalrx-components-2.0/lib/forms";

import { PAQuestionnaireForm } from "@/components";
import { Auth } from "@/api/interfaces";


export type Responses = { [key: string]: string | boolean | null };
export type QuestionPathStatus = "in_path" | "not_applicable" | "possibly_applicable";
export const ANSWER_NOT_AVAILABLE = "_";


const getNextForNumericQuestion = (answerValue: number, questionNext: Record<string, DeterminationContent | string>) => {
  let next = null;
  for (const interval of Object.keys(questionNext)) {
    const range = interval.slice(1, -1).split(",");
    const lowerBound = range[0] === "-inf" ? -Infinity : parseInt(range[0]);
    const upperBound = range[1] === "inf" ? Infinity : parseInt(range[1]);
    const lowerComparison = Array.from(interval)[0];
    const upperComparison = interval.slice(-1);

    if (
      (lowerComparison === "(" && answerValue > lowerBound && upperComparison === ")" && answerValue < upperBound)
        || (lowerComparison === "(" && answerValue > lowerBound && upperComparison !== ")" && answerValue <= upperBound)
        || (lowerComparison !== "(" && answerValue >= lowerBound && upperComparison === ")" && answerValue < upperBound)
        || (lowerComparison !== "(" && answerValue >= lowerBound && upperComparison !== ")" && answerValue <= upperBound)
    ) {
      next = questionNext[interval];
      break;
    } else {
      next = questionNext["default"];
    }
  }
  return next;
};

export function getNext(questions: ReadonlyArray<Question>, question: Question, answer: string | null): DeterminationContent | string | null {
  let next;
  if (answer == null) {
    next = null;
  } else if (question?.question_type == "numeric") {
    const answerValue = parseInt(answer);
    next = getNextForNumericQuestion(answerValue, question.next);

  } else {
    next = question["next"][answer] || question["next"]["default"];
  }

  // answer without next specified, default to next in order
  if (next == null) {

    const currentIdx = questions.indexOf(question);
    if (currentIdx < questions.length - 2) {
      next = questions[currentIdx + 1].question_id;
    }
  }

  return next || null;

}

export function getNextQuestion(questions: ReadonlyArray<Question>, question: Question, answer: string | null): Question | null {

  const next = getNext(questions, question, answer);

  // terminal question
  if (typeof next === "object") {
    return null;
  }

  if (next == null) {
    return null;
  }

  return questions.find((q) => q.question_id == next) || null;

}

const getVisitedNodes = (questions: ReadonlyArray<Question>, answers: { [key: string]: string }) => {
  const nodesToVisit: Question[] = [questions[0]];
  const visitedNodes: Question[] = [];
  function addNodeToVisit(nodeToVisit: Question | null | undefined) {
    if (nodeToVisit && !visitedNodes.includes(nodeToVisit) && !nodesToVisit.includes(nodeToVisit)) {
      nodesToVisit.push(nodeToVisit);
    }
  }
  while (nodesToVisit.length) {
    const currentNode = nodesToVisit.shift();
    if (!currentNode) {
      nodesToVisit.splice(0, nodesToVisit.length);
      break;
    }
    visitedNodes.push(currentNode);
    const answer = answers[currentNode.question_id];
    if (currentNode.question_type == "freeform") {
      addNodeToVisit(getNextQuestion(questions, currentNode, null));
    } else if (answer === ANSWER_NOT_AVAILABLE || answer == null) {
      if (currentNode["next"] == null) {
        addNodeToVisit(getNextQuestion(questions, currentNode, null));
      } else {
        Object.values(currentNode["next"]).forEach((next) => {
          if (typeof next !== "object") {
            addNodeToVisit(questions.find((q) => q.question_id == next));
          }
        });
      }
    } else {
      addNodeToVisit(getNextQuestion(questions, currentNode, answer));
    }
  }

  return visitedNodes;
};

const getQuestionsInPath = (questions: ReadonlyArray<Question>, answers: { [key: string]: string }) => {
  let newDetermination: DeterminationContent | null = null;
  let currentQuestion: Question | null = questions[0];
  const questionsInPath: Question[] = [];
  for (let loops = 0; currentQuestion; loops++) {
    if (loops > 200) {
      console.error("too many loops, something is wrong");
      break;
    }
    const answer = answers[currentQuestion.question_id];

    if (!answer) {
      break;
    }

    questionsInPath.push(currentQuestion);

    if (answer === ANSWER_NOT_AVAILABLE) {
      break;
    } else {
      const next = getNext(questions, currentQuestion, answer);
      if (typeof next === "object") {
        newDetermination = next;
        break;
      } else if (next != null) {
        currentQuestion = questions.find((q) => q.question_id == next);
      } else {
        break;
      }
    }
  }

  return { questionsInPath, newDetermination };
};

export function getQuestionStatus(
  questions: ReadonlyArray<Question>, answers: { [key: string]: string },
): [{ [key: string]: QuestionPathStatus }, Question[], DeterminationContent | null] {

  questions = questions?.filter(Boolean);
  if (!questions?.length) {
    return [{}, [], null];
  }

  const visitedNodes = getVisitedNodes(questions, answers);

  const { questionsInPath, newDetermination } = getQuestionsInPath(questions, answers);

  const newQuestionStatus: { [key: string]: QuestionPathStatus } = Object.fromEntries(questions.map((question) => {
    if (questionsInPath.includes(question)) {
      return [question.question_id, "in_path"];
    } else if (visitedNodes.includes(question)) {
      return [question.question_id, "possibly_applicable"];
    } else {
      return [question.question_id, "not_applicable"];
    }
  }));

  return [newQuestionStatus, questionsInPath, newDetermination];
}

export const getQuestionnaireResponses = (
  formData: Responses,
  questionnaires: QuestionnaireListResponse,
): QuestionnaireResponseSetCreateRequest[] | null => {
  const responseSet = [];

  if (!questionnaires?.results) {
    return responseSet;
  }

  for (const { questionnaire } of questionnaires.results) {
    const responses = questionnaire.questions.filter(
      (question) => formData[`${questionnaire.id}_${question.question_id}`] != null,
    ).map(
      (question) => {
        const { question_id, question_type } = question;
        const answer_choice = formData[`${questionnaire.id}_${question_id}`] as string;

        let request: QuestionnaireResponsesRequest = {
          question_id,
          answer_choice,
        };
        if (question_type !== "freeform" && answer_choice === "other") {
          request = {
            ...request,
            other: formData[`${questionnaire.id}_${question_id}_other`] as string,
          };
        }
        return request;
      },
    );

    if (!responses?.length) {
      continue;
    }
    responseSet.push(
      {
        questionnaire_id: questionnaire.id,
        responses: responses,
      },
    );
  }

  return responseSet;
};


export const prefillQuestionnaireAnswers = (
  form: UseFormReturn<PAQuestionnaireForm>,
  questionnaires: QuestionnaireListResponse,
  prefillAnswers: Auth.QuestionAnswers,
) => {

  if (!questionnaires?.results) {
    return;
  }

  for (const { questionnaire } of questionnaires.results) {
    if (!questionnaire.questions?.length) {
      continue;
    }
    for (const question of questionnaire.questions) {
      const lowerQuestion = question.question_text.toLowerCase();
      if (
        prefillAnswers?.diabetes2
        && question.question_type === "yes_no"
        && lowerQuestion.includes("diagnosis")
        && lowerQuestion.includes("diabetes")
        && lowerQuestion.includes("type 2")
      ) {
        form.setValue(`${questionnaire.id}_${question.question_id}`, "yes");
      } else if (
        prefillAnswers?.diabetes2drugs
        && question.question_type === "yes_no"
        && lowerQuestion.includes("biguanide")
        && lowerQuestion.includes("dpp-4")
        && lowerQuestion.includes("sulfonylurea")
        && lowerQuestion.includes("thiazolidinedione")
      ) {
        form.setValue(`${questionnaire.id}_${question.question_id}`, "yes");
      }
    }
  }

};
